/**
 * `<playing-card-pile>`: the stock, the discard pile, or a face-down Foot.
 *
 * The browser only ever knows a pile's size and, for the discard pile, its top
 * card - the server never sends anything deeper. So the pile draws at most
 * three cards, which is all a squared stack shows anyway, and says the count.
 */

import type { Card } from "@ryzun/haf-saloon-game-engine";
import { type Child, h, setAttribute } from "../dom.js";
import { BaseElement } from "./base-element.js";
import styles from "./card-pile.css";
import "./playing-card.js";

export type PileType = "discard" | "foot" | "stock";

/** Cards drawn in a squared pile; anything deeper is hidden underneath. */
const VISIBLE_DEPTH = 3;

export class CardPile extends BaseElement {
    #type: PileType = "stock";

    #count = 0;

    #top: Card | null = null;

    #frozen = false;

    constructor() {
        super(styles);
    }

    get type(): PileType {
        return this.#type;
    }

    set type(value: PileType) {
        this.#type = value;
        this.invalidate();
    }

    get count(): number {
        return this.#count;
    }

    set count(value: number) {
        this.#count = value;
        this.invalidate();
    }

    /** The face-up card on a discard pile; ignored by the other pile types. */
    get top(): Card | null {
        return this.#top;
    }

    set top(value: Card | null) {
        this.#top = value;
        this.invalidate();
    }

    get frozen(): boolean {
        return this.#frozen;
    }

    set frozen(value: boolean) {
        this.#frozen = value;
        this.invalidate();
    }

    protected render(): void {
        setAttribute(this, "type", this.#type);
        setAttribute(this, "aria-hidden", "true");

        const depth = Math.min(this.#count, VISIBLE_DEPTH);
        const showsTop = this.#type === "discard" && this.#top !== null;
        const cards = Array.from({ length: depth }, (_, index) => {
            const card = document.createElement("playing-card");
            card.card = showsTop && index === depth - 1 ? this.#top : null;

            return card;
        });

        this.root.replaceChildren(h(
            "div",
            { class: "pile" },
            depth === 0 && h("span", { class: "vacant" }),
            ...cards,
            this.#renderCaption()
        ));
    }

    #renderCaption(): Child {
        if (this.#type === "foot") {
            return null;
        }

        const name = this.#type === "stock" ? "Stock" : "Discard";

        return h(
            "span",
            { class: "caption" },
            `${name} · `,
            h("span", { class: "count" }, String(this.#count)),
            this.#type === "discard" && this.#frozen && h("span", { class: "frozen" }, "FROZEN")
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "playing-card-pile": CardPile;
    }
}

customElements.define("playing-card-pile", CardPile);
